/**
 * Money display.
 *
 * Colour NEVER carries the meaning on its own — the sign is always in the
 * text. Roughly one man in twelve cannot reliably tell the red from the green,
 * and "is this money in or out" is not something to leave to a hue.
 *
 * A transfer or exchange is rendered NEUTRAL, not green or red. Moving money
 * between your own pockets is neither a gain nor a loss, and colouring it
 * either way is the most common lie a personal-finance UI tells.
 */
import { cn } from '@/lib/utils';
import { formatAmount, formatMoney, currencySymbol } from '@/lib/format';
import type { TransactionKind } from '@/lib/types';

export function Money({
  minor,
  currency,
  decimals = 2,
  className,
  signed = false,
  tone,
}: {
  minor: number;
  currency: string;
  decimals?: number;
  className?: string;
  /** Show an explicit + / − prefix. */
  signed?: boolean;
  /** Override the colour. 'auto' derives it from the sign. */
  tone?: 'auto' | 'income' | 'expense' | 'neutral' | 'plain';
}): JSX.Element {
  const resolved = tone ?? 'plain';
  const colour =
    resolved === 'plain'
      ? ''
      : resolved === 'neutral'
        ? 'text-muted-foreground'
        : resolved === 'income'
          ? 'text-income'
          : resolved === 'expense'
            ? 'text-expense'
            : minor > 0
              ? 'text-income'
              : minor < 0
                ? 'text-expense'
                : 'text-muted-foreground';

  const sign = signed ? (minor > 0 ? '+' : minor < 0 ? '−' : '') : minor < 0 ? '−' : '';
  const body = formatAmount(Math.abs(minor), decimals);

  return (
    <span className={cn('tabular whitespace-nowrap', colour, className)}>
      {sign}
      {body}
      <span className="ml-1 opacity-70">{currencySymbol(currency)}</span>
    </span>
  );
}

/** The colour a transaction kind should use. Single source of truth. */
export function toneForKind(kind: TransactionKind): 'income' | 'expense' | 'neutral' {
  switch (kind) {
    case 'INCOME':
      return 'income';
    case 'EXPENSE':
      return 'expense';
    case 'TRANSFER':
    case 'EXCHANGE':
      return 'neutral';
    case 'ADJUSTMENT':
      return 'neutral';
  }
}

export const KIND_LABELS: Record<TransactionKind, string> = {
  INCOME: 'Приход',
  EXPENSE: 'Разход',
  TRANSFER: 'Прехвърляне',
  EXCHANGE: 'Обмяна',
  ADJUSTMENT: 'Корекция',
};

/**
 * A fund's or location's holdings across currencies.
 *
 * Shows the native amounts and, only when more than one currency is involved,
 * the base-currency equivalent with a ≈ — the approximation is real (today's
 * rate) and pretending otherwise would be dishonest.
 */
export function MultiCurrency({
  totals,
  baseMinor,
  baseCurrency,
  baseDecimals = 2,
  className,
  compact = false,
}: {
  totals: Array<{ currency: string; decimals: number; nativeMinor: number }>;
  baseMinor: number;
  baseCurrency: string;
  baseDecimals?: number;
  className?: string;
  compact?: boolean;
}): JSX.Element {
  const meaningful = totals.filter((t) => t.nativeMinor !== 0);
  const onlyBase =
    meaningful.length <= 1 && (meaningful[0]?.currency ?? baseCurrency) === baseCurrency;

  if (onlyBase) {
    return (
      <span className={cn('tabular', className)}>
        {formatMoney(baseMinor, baseCurrency, baseDecimals)}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5', className)}>
      {meaningful.map((t) => (
        <span key={t.currency} className="tabular">
          {formatMoney(t.nativeMinor, t.currency, t.decimals)}
        </span>
      ))}
      <span className={cn('tabular text-muted-foreground', compact ? 'text-xs' : 'text-sm')}>
        ≈ {formatMoney(baseMinor, baseCurrency, baseDecimals)}
      </span>
    </span>
  );
}
